import { setSentryMember } from "@configs/sentry/sentry";
import { STORAGE_KEY } from "@constants";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import logger from "@utils/logger";
import {
  AccountModel,
  AuthToken,
  BankConfig,
  bnOrZero,
  CredentialModel,
  MemberFundingProfileSettings,
  ProfileResponse,
  SelfEntityModel,
  WalletType,
} from "kz-ui-sdk";
import { InitAuthPayload } from "./types";

export type AuthSliceState = {
  oauth?: AuthToken | null;
  self?: SelfEntityModel | null;
  credential?: CredentialModel | null;
  bankInfos?: Record<string, BankConfig> | null;
  ewalletInfos?: Record<string, BankConfig> | null;
  accounts?: AccountModel[] | null;
  contactLink?: string | null;
  latestDepositAmount?: number;
  isRefreshingToken?: boolean;
  fundingProfileSettings?: MemberFundingProfileSettings;
};

const persistToken = (token: AuthToken | null) => {
  logger._console.log("persist token", token);
  if (token?.access_token) localStorage.setItem(STORAGE_KEY.ACCESS_TOKEN, token.access_token);
  else localStorage.removeItem(STORAGE_KEY.ACCESS_TOKEN);
};

const clearOnboardStorage = (phone: string) => {
  localStorage.removeItem(STORAGE_KEY.OTP_TIMEOUT_PREFIX + phone);
  sessionStorage.removeItem(STORAGE_KEY.ONBOARDING_PHONE);
};

const generateSelf = (data: SelfEntityModel | undefined): SelfEntityModel | null => {
  let self = data ? { ...data } : null;
  if (self?.member?.phone) {
    // Clear onboard storage
    clearOnboardStorage(self?.member?.phone);
  }
  // trim fullname
  if (self?.member?.fullname) {
    self = {
      ...self,
      member: {
        ...self.member,
        fullname: self.member.fullname.trim(),
      },
    };
  }
  return self;
};

export const initialState: AuthSliceState = {};

export const authSlice = createSlice({
  name: "authSlices",
  initialState,
  reducers: {
    initAuthState(state, action: PayloadAction<InitAuthPayload | null>) {
      const { token = null, accounts = null, credential = null } = action.payload ?? {};
      const self = generateSelf(action.payload?.self);
      state.oauth = token;
      state.self = self;
      state.bankInfos = action.payload?.bankInfos;
      state.ewalletInfos = action.payload?.ewalletInfos;
      state.contactLink = action.payload?.contactLink;
      state.latestDepositAmount = action.payload?.latestDepositAmount;
      state.credential = credential;
      state.fundingProfileSettings = action.payload?.fundingProfileSettings;
      persistToken(token);
      setSentryMember(self?.member);
    },
    updateAccessToken(state, action: PayloadAction<AuthToken | null>) {
      logger._console.log("update access token", action);
      state.oauth = action.payload;
      persistToken(action.payload);
    },
    resetExpiredAt(state) {
      if (state?.self?.member?.pwExpireAt) {
        state.self.member.pwExpireAt = null;
      }
    },
    updateProfile(state, action: PayloadAction<ProfileResponse | null>) {
      const self = generateSelf(action.payload?.self);
      state.accounts = action.payload?.accounts ?? null;
      state.self = self;
      state.contactLink = action.payload?.contactLink ?? "";
      state.bankInfos = action.payload?.bankInfos;
      state.ewalletInfos = action.payload?.ewalletInfos;
      state.latestDepositAmount = action.payload?.latestDepositAmount;
      state.credential = action.payload?.credential;
      state.fundingProfileSettings = action.payload?.fundingProfileSettings;
      setSentryMember(self?.member);
    },
    updateProfileBalance(state, action: PayloadAction<string>) {
      // create temp primaryWallet if not exist
      if (state.self?.member && !state.self?.member?.primaryWallet) {
        state.self.member.primaryWallet = {
          balance: bnOrZero(action.payload),
          accountId: state.self.member.accountId,
          memberId: state.self.member.id,
          type: WalletType.DEFAULT,
          txCount: 0,
          id: state.self.member.id,
        };
      }
      // update state.self.member.primaryWallet.balance with new balance
      if (state.self?.member?.primaryWallet) {
        state.self.member.primaryWallet.balance = bnOrZero(action.payload);
      }
    },
    logout(state, action: PayloadAction<void>) {
      state.oauth = null;
      state.self = null;
      state.accounts = null;
      state.contactLink = null;
      state.credential = null;
      setSentryMember(null);
      persistToken(null);
    },
    progressRefreshAccessToken(
      state,
      action: PayloadAction<{
        isRefreshingToken: boolean;
      }>,
    ) {
      state.isRefreshingToken = action.payload.isRefreshingToken;
    },
  },
});

export default authSlice;
